import React, { useEffect, useState, useRef, useCallback } from 'react';
import AsyncSelect from "react-select/async"; 
import Select from "react-select"; 
import { useNavigate } from "react-router-dom";
import { customFetch } from '../apiClient';

const EmployeeForm = ({ onSubmitSuccess }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    type: "",
    specialization: "",
    degree: "",
    contact1: "",
    address1: "",
    department: null,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const formRef = useRef(null);

  const employeeTypes = [
    { value: "Doctor", label: "Doctor" },
    { value: "Nurse", label: "Nurse" },
    { value: "Staff", label: "Staff" },
    { value: "RMO", label: "RMO" },
  ];

  const loadDepartmentOptions = async (inputValue) => {
    try {
     const res = await customFetch(`/Departments?search=${inputValue || ""}`);
      if (!res.ok) throw new Error("Failed to load departments");
      const data = await res.json();
      return data.map((d) => ({ label: d.departmentName, value: d.srno }));
    } catch {
      return [];
    }
  };

  useEffect(() => {
    if (formRef.current) {
      document.getElementById('name')?.focus();
    }
  }, []);

  // ✅ PERFECTED KEYBOARD NAVIGATION LOGIC
  const handleKeyDown = useCallback((e) => {
    const isReactSelect = e.target.id === 'type' || e.target.id === 'department';

    // 1. Allow React-Select to handle Up/Down arrow for its dropdown menu naturally
    if (isReactSelect && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      return;
    }

    // 2. Allow React-Select to use Enter to select an option if menu is open
    if (isReactSelect && e.key === 'Enter') {
      const isMenuExpanded = e.target.getAttribute('aria-expanded') === 'true';
      if (isMenuExpanded) {
        return;
      }
    }

    // 3. Strictly ordered array of inputs using explicit IDs to prevent skipping
    const focusableElements = Array.from(
      formRef.current?.querySelectorAll(
        'input[id="name"], input[id="type"], input[id="contact1"], input[id="specialization"], input[id="degree"], input[id="department"], textarea[id="address1"], button[id="submitBtn"]'
      ) || []
    );

    if (focusableElements.length === 0) return;

    const currentIndex = focusableElements.indexOf(e.target);
    if (currentIndex === -1) return;

    // 4. Submit form if Enter is pressed on the submit button
    if (e.key === 'Enter' && currentIndex === focusableElements.length - 1) {
      e.target.closest('form').requestSubmit();
      return;
    }

    // 5. Navigate strictly to the next/prev element in the ordered array
    if (['Enter', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextElement = focusableElements[currentIndex + 1];
      nextElement?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevElement = focusableElements[currentIndex - 1];
      prevElement?.focus();
    }
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (formRef.current && e.target.closest('form') === formRef.current) {
        handleKeyDown(e);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Required';
        if (value.length < 3) return 'Min 3 chars';
        return '';
      case 'type':
        if (!value) return 'Required';
        return '';
      case 'degree':
        if (!value.trim()) return 'Required';
        return '';
      case 'contact1':
        if (value && !/^\+?\d{10,15}$/.test(value.replace(/\s/g, ''))) 
          return 'Invalid number';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    
    if (touched[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setFieldErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleTypeChange = (option) => {
    const value = option ? option.value : "";
    setForm(prev => ({ ...prev, type: value }));
    if (touched.type) {
        const error = validateField('type', value);
        setFieldErrors(prev => ({ ...prev, type: error }));
    }
    // Auto-advance to Contact field upon selection
    setTimeout(() => document.getElementById('contact1')?.focus(), 0);
  };

  const handleDepartmentChange = (option) => {
    setForm((prev) => ({
      ...prev,
      department: option ? { srno: option.value, departmentName: option.label } : null,
    }));
    // Auto-advance to Address field upon selection
    setTimeout(() => document.getElementById('address1')?.focus(), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const errors = {};
    errors.name = validateField('name', form.name);
    errors.type = validateField('type', form.type);
    errors.degree = validateField('degree', form.degree);
    errors.contact1 = validateField('contact1', form.contact1);

    const hasErrors = Object.values(errors).some(err => err !== '');
    if (hasErrors) {
      setFieldErrors(errors);
      setTouched({ name: true, type: true, degree: true, contact1: true });
      setError('Please fix form errors');
      return;
    }

    if (!form.department) {
      setError("Please select a department");
      return;
    }

    setLoading(true);
    try {
      const payload = { ...form };

      const res = await customFetch(`/Employees`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Employee creation failed");
      }

      setSuccess("Employee added successfully!");
      setForm({ name: "", type: "", specialization: "", degree: "", contact1: "", address1: "", department: null });
      setTouched({});
      setFieldErrors({});
      if (onSubmitSuccess) onSubmitSuccess();
      
      document.getElementById('name')?.focus();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = () => {
    navigate('/master/employee-list');
  };


  const handleReset = () => {
    setForm({ name: "", type: "", specialization: "", degree: "", contact1: "", address1: "", department: null });
    setTouched({});
    setFieldErrors({});
    setError('');
    setSuccess('');
    document.getElementById('name')?.focus();
  };

  const getFieldClass = (fieldName) => {
    if (!touched[fieldName]) return 'form-control-custom';
    if (fieldErrors[fieldName]) return 'form-control-custom error';
    if (form[fieldName]) return 'form-control-custom success';
    return 'form-control-custom';
  };

  // Compacted React-Select Styles
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '30px', height: '30px', borderRadius: '4px',
      border: state.isFocused ? '1px solid var(--theme-color, #0088cc)' : '1px solid #cbd5e0',
      boxShadow: state.isFocused ? '0 0 0 1px var(--theme-color, #0088cc)' : 'none', 
      '&:hover': { border: '1px solid var(--theme-color, #0088cc)' },
      backgroundColor: 'white', fontSize: '12px'
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: '30px' }),
    menu: (base) => ({
      ...base, borderRadius: '4px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e0e6ed', zIndex: 9999, fontSize: '12px'
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? 'var(--theme-color, #0088cc)' : state.isFocused ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' : 'white',
      color: state.isSelected ? 'white' : '#2d3748',
      cursor: 'pointer', padding: '6px 10px',
      '&:active': { backgroundColor: 'var(--theme-color, #0088cc)' },
    }),
  };

  return (
    <div className="employee-form-container">
      <div className="employee-card">
        {/* Header */}
        <div className="employee-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-person-badge-fill"></i>
            </div>
            <div className="header-text">
              <h5>Add New Employee</h5>
              <p>Register employee details</p>
            </div>
          </div>
          <button className="btn-view-list" onClick={handleButtonClick} title="View Employees" tabIndex="-1">
            <i className="bi bi-list-ul"></i> Employee List
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError('')} tabIndex="-1"><i className="bi bi-x"></i></button>
          </div>
        )}
        {success && (
          <div className="alert-custom alert-success">
            <i className="bi bi-check-circle-fill"></i>
            <span>{success}</span>
            <button className="alert-close" onClick={() => setSuccess('')} tabIndex="-1"><i className="bi bi-x"></i></button>
          </div>
        )}

        <form ref={formRef} onSubmit={handleSubmit} className="employee-form">
          <div className="form-section">
             <div className="form-grid">
               
               {/* --- ROW 1 --- */}
               <div className="form-group">
                  <label htmlFor="name"><i className="bi bi-person"></i> Full Name <span className="req">*</span></label>
                  <div className="input-wrapper">
                    <input
                      id="name" name="name" type="text"
                      value={form.name} onChange={handleChange} onBlur={handleBlur}
                      placeholder="Enter name" className={getFieldClass('name')} 
                      tabIndex={1} required disabled={loading}
                    />
                  </div>
                  {touched.name && fieldErrors.name && <small className="error-text">{fieldErrors.name}</small>}
               </div>

               <div className="form-group">
                  <label htmlFor="type"><i className="bi bi-briefcase"></i> Role Type <span className="req">*</span></label>
                  <Select
                    inputId="type" /* ✅ Links ID to the actual hidden input for strict Focus mapping */
                    options={employeeTypes} onChange={handleTypeChange}
                    value={employeeTypes.find(opt => opt.value === form.type) || null}
                    placeholder="Select role..." classNamePrefix="react-select"
                    styles={customSelectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                    tabIndex={2} isDisabled={loading}
                  />
                  {touched.type && fieldErrors.type && <small className="error-text">{fieldErrors.type}</small>}
               </div>

               <div className="form-group">
                  <label htmlFor="contact1"><i className="bi bi-phone"></i> Contact</label>
                  <div className="input-wrapper">
                    <input
                      id="contact1" name="contact1" type="text" maxLength={15}
                      value={form.contact1} onChange={handleChange} onBlur={handleBlur}
                      placeholder="e.g. 98XXXXXXXX" className={getFieldClass('contact1')} 
                      tabIndex={3} disabled={loading}
                    />
                  </div>
                  {touched.contact1 && fieldErrors.contact1 && <small className="error-text">{fieldErrors.contact1}</small>}
               </div>

               {/* --- ROW 2 --- */}
               <div className="form-group">
                  <label htmlFor="specialization"><i className="bi bi-star"></i> Specialization</label>
                  <input
                    id="specialization" name="specialization" type="text"
                    value={form.specialization} onChange={handleChange}
                    placeholder="e.g. Cardiology" className="form-control-custom" 
                    tabIndex={4} disabled={loading}
                  />
               </div>

               <div className="form-group">
                  <label htmlFor="degree"><i className="bi bi-mortarboard"></i> Degree <span className="req">*</span></label>
                  <div className="input-wrapper">
                    <input
                      id="degree" name="degree" type="text"
                      value={form.degree} onChange={handleChange} onBlur={handleBlur}
                      placeholder="e.g. MBBS, MD" className={getFieldClass('degree')} 
                      tabIndex={5} required disabled={loading}
                    />
                  </div>
                  {touched.degree && fieldErrors.degree && <small className="error-text">{fieldErrors.degree}</small>}
               </div>

               <div className="form-group">
                  <label htmlFor="department"><i className="bi bi-building"></i> Department <span className="req">*</span></label>
                  <AsyncSelect
                    inputId="department" /* ✅ Links ID to the actual hidden input for strict Focus mapping */
                    cacheOptions defaultOptions loadOptions={loadDepartmentOptions}
                    onChange={handleDepartmentChange}
                    value={form.department ? { label: form.department.departmentName, value: form.department.srno } : null}
                    isClearable placeholder="Search dept..." styles={customSelectStyles}
                    menuPortalTarget={document.body} menuPosition="fixed" 
                    tabIndex={6} isDisabled={loading}
                  />
               </div>

               {/* --- ROW 3 --- */}
               <div className="form-group full-width">
                  <label htmlFor="address1"><i className="bi bi-house"></i> Residential Address</label>
                  <textarea
                    id="address1" name="address1" value={form.address1} onChange={handleChange}
                    placeholder="Enter full address" className="form-textarea-custom" 
                    rows={2} tabIndex={7} disabled={loading}
                  ></textarea>
               </div>
             </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleReset} disabled={loading} tabIndex="-1">
               Reset
            </button>
            <button id="submitBtn" type="submit" className="btn-primary" disabled={loading} tabIndex={8}>
              {loading ? 'Saving...' : 'Save Employee'}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .employee-form-container { padding: 0; font-family: system-ui, -apple-system, sans-serif; }
        .employee-card {
          background: white; border-radius: 6px; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0; overflow: visible;
        }

        /* Header */
        .employee-header {
          background: var(--theme-color, #0088cc); padding: 8px 12px;
          display: flex; justify-content: space-between; align-items: center;
          border-bottom: 2px solid #00d4aa; flex-wrap: wrap; gap: 10px;
        }
        
        .header-left { display: flex; align-items: center; gap: 8px; }
        .header-icon {
          width: 28px; height: 28px; background: rgba(255, 255, 255, 0.2);
          border-radius: 4px; display: flex; align-items: center; justify-content: center;
          font-size: 14px; color: white;
        }
        .header-text h5 { margin: 0; color: white; font-size: 14px; font-weight: 600; }
        .header-text p { margin: 0; color: rgba(255,255,255,0.85); font-size: 10px; }

        .btn-view-list {
          padding: 4px 10px; border-radius: 4px; border: 1px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.15); color: white; font-size: 11px; font-weight: 600;
          cursor: pointer; transition: 0.2s; display: flex; align-items: center; gap: 5px;
        }
        .btn-view-list:hover { background: white; color: var(--theme-color, #0088cc); }

        /* Alerts */
        .alert-custom {
          margin: 8px 12px 0; padding: 6px 10px; border-radius: 4px;
          display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 500; position: relative;
        }
        .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }
        .alert-close { position: absolute; right: 8px; background: none; border: none; cursor: pointer; opacity: 0.6; }

        /* Form Layout */
        .employee-form { padding: 12px; }
        .form-section { background: #f8fafc; padding: 12px; border-radius: 4px; border: 1px solid #e2e8f0; }
        
        /* Grid Layout */
        .form-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        
        .form-group { display: flex; flex-direction: column; gap: 4px; }
        .form-group.full-width { grid-column: 1 / -1; }
        
        .form-group label {
          font-size: 11px; font-weight: 600; color: #4a5568; display: flex; align-items: center; gap: 4px;
        }
        .form-group label i { color: var(--theme-color, #0088cc); }
        .req { color: #e53e3e; }
        
        .input-wrapper { position: relative; }

        /* Inputs */
        .form-control-custom, .form-textarea-custom {
          width: 100%; padding: 6px 8px; border: 1px solid #cbd5e0; border-radius: 4px;
          font-size: 12px; color: #2d3748; outline: none; transition: 0.2s; background: white;
        }
        .form-textarea-custom { min-height: 40px; resize: vertical; }

        .form-control-custom:focus, .form-textarea-custom:focus {
          border-color: var(--theme-color, #3182ce); box-shadow: 0 0 0 1px var(--theme-color, #3182ce);
        }

        .form-control-custom.success { border-color: #38a169; background: #f0fff4; }
        .form-control-custom.error { border-color: #e53e3e; background: #fff5f5; }
        
        .error-text { color: #e53e3e; font-size: 10px; margin-top: 2px; }

        /* Buttons */
        .form-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; border-top: 1px solid #e2e8f0; padding-top: 10px; }
        .btn-primary, .btn-secondary {
          padding: 6px 14px; border-radius: 4px; font-size: 12px; font-weight: 600; border: none; cursor: pointer; transition: 0.2s;
        }
        .btn-primary { background: var(--theme-color, #3182ce); color: white; }
        .btn-primary:hover:not(:disabled) { opacity: 0.9; }
        .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-secondary { background: #edf2f7; color: #4a5568; border: 1px solid #cbd5e0; }
        .btn-secondary:hover:not(:disabled) { background: #e2e8f0; }

        /* Responsive Breakpoints */
        @media (max-width: 992px) {
          .form-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 576px) {
          .employee-header { flex-direction: column; align-items: stretch; text-align: center; }
          .header-left { justify-content: center; }
          .form-grid { grid-template-columns: 1fr; }
          .form-actions { flex-direction: column; }
          .btn-primary, .btn-secondary { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default EmployeeForm;